import classNames from "classnames";
import produce from "immer";
import Button from "kustom-front/src/components/atoms/Button";
import { ReactComponent as TrashIcon } from "kustom-front/src/icons/trash.svg";
import React, { FunctionComponent } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import QuantitySelect from "@Components/atoms/cart/QuantitySelect";
import CheckboxWithLabel from "@Components/molecules/cart/CheckboxWithLabel";
import useCartContext from "@Hooks/useCartContext";
import CheerzApiCart from "@Types/api/cart";
import { Article } from "@Types/cart";
import { convertFromApiToCartContent, convertFromCartContentToApi } from "@Utils/cart/cart";
import { convertToOrderUpdatedProperties, setDataLayerTrack } from "@Utils/track";
import {
  getDefaultByValueTag,
  getUpsellCompleteTag,
  getUpsellsToDisplay,
  getUpsellValues,
  ignoredOptionTag,
  ignoredProductTag,
} from "@Utils/upsells";
import "./style.scss";

interface Props {
  article: Article;
  disabled: boolean;
  onChangeGift: (id: number) => void;
  onChangeQuantity: (id: number, quantity: number) => void;
  onEdit: (id: number) => void;
  onRemove: (article: Article) => void;
}

interface ContainerProps {
  article: Article;
  disabled: boolean;
  onRemove: (article: Article) => void;
}

export const CartArticle: FunctionComponent<Props> = ({
  article,
  disabled,
  onChangeGift,
  onChangeQuantity,
  onEdit,
  onRemove,
}) => {
  let AdditionalOptionsList = null;
  const optionsList = getUpsellsToDisplay(article.additionalOptions, article.product.tag);
  const intl = useIntl();

  if (optionsList.length > 0) {
    AdditionalOptionsList = (
      <div>
        <div className="CartArticle-content-title">
          <FormattedMessage id="cheerz_CartArticle_include" />
        </div>
        <ul className="CartArticle-content-list">
          {optionsList.map((option) => {
            const values = getUpsellValues(option, article.total, intl);
            if (
              ignoredOptionTag(option.productOptionTag) ||
              ignoredProductTag(article.product.tag)
            ) {
              return null;
            }

            const optionTag = getUpsellCompleteTag(option.productOptionTag, option.optionValueTag);
            let translationId = `cheerz_UpsellsModal_title__${optionTag}`;
            if (intl.messages[translationId] === undefined) {
              const optionValue = getDefaultByValueTag(option.optionValueTag);
              if (intl.messages[`cheerz_UpsellsModal_title__${optionValue}`]) {
                translationId = `cheerz_UpsellsModal_title__${optionValue}`;
              } else {
                translationId = `cheerz_UpsellsModal_title__${option.productOptionTag}`;
              }
            }
            return (
              <li key={optionTag}>
                <FormattedMessage id={translationId} values={values} />
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const handleChangeGift = () => {
    onChangeGift(article.id);
  };

  const handleChangeQuantity = (quantity: number) => {
    onChangeQuantity(article.id, quantity);
  };

  const handleEdit = () => {
    onEdit(article.id);
  };

  const handleRemove = () => {
    onRemove(article);
  };

  let info;
  if (article.product.outOfStock) {
    info = (
      <div className="CartArticle-outOfStock-info">
        <FormattedMessage id="cheerz_CartArticle_out_of_stock" />
      </div>
    );
  } else {
    info = (
      <CheckboxWithLabel checked={article.gift} disabled={disabled} onChange={handleChangeGift}>
        <FormattedMessage id="cheerz_CartArticle_hide_price" />
      </CheckboxWithLabel>
    );
  }

  const fixedProducts = ["printable-gift", "gift"];
  const hasEditButton = !fixedProducts.includes(article.product.tag);

  return (
    <div
      className={classNames("CartArticle", {
        "CartArticle--outOfStock": article.product.outOfStock,
      })}
    >
      <div className="CartArticle-picture">
        <img src={article.product.productImage.small} alt={article.product.name} />
      </div>
      <div className="CartArticle-content">
        <div className="CartArticle-name">{article.product.name}</div>
        <div className="CartArticle-content-container">
          {article.selectionContent && (
            <div className="CartArticle-items-summary">
              <FormattedMessage
                id="cheerz_CartArticle_pictures_count"
                values={{ count: article.selectionContent.length }}
              />
            </div>
          )}
          {AdditionalOptionsList}
        </div>
        <div className="CartArticle-content-container CartArticle-content-container-actions">
          {hasEditButton && (
            <Button type="primary-inverted" onClick={handleEdit}>
              <FormattedMessage id="cheerz_CartArticle_edit" />
            </Button>
          )}
          <div className="CartArticle-content-container-quantity">
            <QuantitySelect
              value={article.quantity}
              disabled={disabled}
              onChange={handleChangeQuantity}
            />
            <div
              className={classNames("CartArticle-price", {
                "CartArticle-price--disabled": disabled,
              })}
            >
              <FormattedNumber
                value={article.total.cents / 100}
                style="currency"
                currency={article.total.currency}
              />
            </div>
          </div>
        </div>
        <div className="CartArticle-content-container">{info}</div>
      </div>
      <div className="CartArticle-remove" onClick={() => handleRemove()}>
        <TrashIcon height={16} width={16} />
      </div>
    </div>
  );
};

const CartArticleContainer: FunctionComponent<ContainerProps> = ({
  article,
  disabled,
  onRemove,
}) => {
  const { cart, configuration, dispatch } = useCartContext();

  const onChangeGiftClick = (id: number) => {
    dispatch({
      disabled: true,
      type: "setDisabledCart",
    });

    const updatedCart = produce(cart, (draftState) => {
      const updatedArticle = draftState.articles.find((drafArticle) => drafArticle.id === id);
      if (updatedArticle) {
        updatedArticle.gift = !updatedArticle.gift;
      }
    });
    const updatedCartJson = convertFromCartContentToApi(updatedCart);

    configuration.apiProviders.cheerz
      .saveCart(updatedCartJson)
      .then((cartResponse: CheerzApiCart) => {
        dispatch({
          cart: {
            ...convertFromApiToCartContent(cartResponse),
          },
          type: "setSavedCart",
        });
        setDataLayerTrack("Order Updated", convertToOrderUpdatedProperties(cart, "Checkout"));
      })
      .catch(() => {
        return;
      });
  };

  const onChangeQuantityClick = (id: number, quantity: number) => {
    dispatch({
      disabled: true,
      type: "setDisabledCart",
    });

    const updatedCart = produce(cart, (draftState) => {
      const updatedArticle = draftState.articles.find((drafArticle) => drafArticle.id === id);
      if (updatedArticle) {
        updatedArticle.quantity = quantity;
      }
    });
    const updatedCartJson = convertFromCartContentToApi(updatedCart);

    configuration.apiProviders.cheerz
      .saveCart(updatedCartJson)
      .then((cartResponse: CheerzApiCart) => {
        dispatch({
          cart: {
            ...convertFromApiToCartContent(cartResponse),
          },
          type: "setSavedCart",
        });
        setDataLayerTrack("Order Updated", convertToOrderUpdatedProperties(cart, "Checkout"));
      })
      .catch(() => {
        return;
      });
  };

  const onEditClick = (id: number) => {
    setDataLayerTrack("Cart Article Edit Clicked", {
      articleId: id,
      reorder: article.recovered ? "Reordered article" : "Not reordered article",
    });
    window.location.href = `customizations/${id}/edit`;
  };

  return (
    <CartArticle
      article={article}
      disabled={disabled}
      onChangeGift={onChangeGiftClick}
      onChangeQuantity={onChangeQuantityClick}
      onEdit={onEditClick}
      onRemove={onRemove}
    />
  );
};

export default CartArticleContainer;
